import { useCallback, useEffect, useMemo, useState } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { FormattedDate, type IntlShape, type MessageDescriptor, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Pressable } from '@atlaskit/primitives/compiled';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { parseDateValue } from '@/common/date-utils/parse-date-value';
import {
	DEFAULT_CONTROLS_PAYLOAD,
	FastshiftTable,
	type ControlsPayload,
	type TableHeads,
	type TableRows,
} from '@/common/fastshift-table';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import { getFastshiftCsProfilerLinkedTickets } from '@/state/actions';
import type { CsProfilerLinkedTicketsState } from '@/state/types';

import { useFastshiftCsProfilerAnalytics } from './hooks/use-fastshift-cs-profiler-analytics';
import { messages } from './messages';

import type {
	FastshiftCsProfilerEntTicket,
	FastshiftCsProfilerJpdTicket,
} from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';

export type FastshiftCsProfilerLinkedTicketsProps = {
	themeName: string;
};

const styles = cssMap({
	linkedTicketsContentWrapper: {
		paddingBlockStart: token('space.100'),
	},
	linkedTicketsToggleButton: {
		backgroundColor: 'transparent',
		borderWidth: 0,
		color: token('color.link'),
		cursor: 'pointer',
		paddingBlock: 0,
		paddingInline: 0,
		textAlign: 'start',
		textDecorationLine: 'underline',
	},
	ticketTableHeader: {
		alignItems: 'center',
		columnGap: token('space.150'),
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'flex-start',
		rowGap: token('space.075'),
	},
	ticketSummary: {
		maxWidth: '360px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const getThemeIdSegment = (themeName: string) =>
	themeName
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-|-$/g, '');

const LINKED_TICKETS_DEFAULT_CONTROLS: ControlsPayload = {
	...DEFAULT_CONTROLS_PAYLOAD,
	size: 5,
};

type SortableValue = number | string | null | undefined;
type LozengeAppearance = NonNullable<React.ComponentProps<typeof Lozenge>['appearance']>;

const getPagination = (controls: ControlsPayload, totalElements: number) => {
	const totalPages = Math.ceil(totalElements / controls.size);

	return {
		page: controls.page,
		size: controls.size,
		totalElements,
		totalPages,
		hasNext: controls.page < totalPages - 1,
		hasPrevious: controls.page > 0,
		sorted: false,
	};
};

const compareSortableValues = (left: SortableValue, right: SortableValue) => {
	if (typeof left === 'number' && typeof right === 'number') {
		return left - right;
	}

	return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
		numeric: true,
		sensitivity: 'base',
	});
};

const sortTickets = <Ticket,>(
	tickets: readonly Ticket[],
	controls: ControlsPayload,
	getValue: (ticket: Ticket, sortBy: string) => SortableValue,
) => {
	if (!controls.sortBy) {
		return tickets;
	}

	const directionMultiplier = controls.sortDirection === 'DESC' ? -1 : 1;

	return tickets.slice().sort((leftTicket, rightTicket) => {
		return (
			compareSortableValues(
				getValue(leftTicket, controls.sortBy ?? ''),
				getValue(rightTicket, controls.sortBy ?? ''),
			) * directionMultiplier
		);
	});
};

const getPriorityLozengeAppearance = (priority: number): LozengeAppearance => {
	if (priority <= 1) {
		return 'removed';
	}

	if (priority === 2) {
		return 'moved';
	}

	if (priority === 3) {
		return 'inprogress';
	}

	if (priority === 4) {
		return 'new';
	}

	return 'default';
};

const getStatusLozengeAppearance = (status: string): LozengeAppearance => {
	const normalizedStatus = status.toLowerCase();

	if (normalizedStatus.includes('blocked') || normalizedStatus.includes('risk')) {
		return 'removed';
	}

	if (normalizedStatus.includes('investigating') || normalizedStatus.includes('pending')) {
		return 'inprogress';
	}

	if (normalizedStatus.includes('paused') || normalizedStatus.includes('review')) {
		return 'moved';
	}

	if (normalizedStatus.includes('roadmap') || normalizedStatus.includes('track')) {
		return 'success';
	}

	if (normalizedStatus.includes('backlog')) {
		return 'new';
	}

	return 'default';
};

const MissingTicketValue = ({ intl }: { intl: IntlShape }) => (
	<Text size="small">{intl.formatMessage(messages.noTicketValue)}</Text>
);

const TicketDate = ({ value, intl }: { value: string | null; intl: IntlShape }) =>
	value ? (
		<Text size="small">
			<FormattedDate value={parseDateValue(value)} year="numeric" month="short" day="numeric" />
		</Text>
	) : (
		<MissingTicketValue intl={intl} />
	);

const LinkedTicketsTableHeading = ({
	message,
	intl,
}: {
	message: MessageDescriptor;
	intl: IntlShape;
}) => (
	<Text as="span" size="small" weight="semibold">
		{intl.formatMessage(message)}
	</Text>
);

const LinkedTicketsTableHeader = ({
	message,
	count,
	intl,
}: {
	message: MessageDescriptor;
	count: number;
	intl: IntlShape;
}) => (
	<Box xcss={styles['ticketTableHeader']}>
		<Text size="small" weight="semibold">
			{intl.formatMessage(message, {
				count,
			})}
		</Text>
	</Box>
);

const EntTicketsTable = ({
	tickets,
	themeName,
	intl,
	onTicketLinkClick,
}: {
	tickets: readonly FastshiftCsProfilerEntTicket[];
	themeName: string;
	intl: IntlShape;
	onTicketLinkClick: (ticketKey: string) => void;
}) => {
	const [controls, setControls] = useState<ControlsPayload>(LINKED_TICKETS_DEFAULT_CONTROLS);

	useEffect(() => {
		setControls(LINKED_TICKETS_DEFAULT_CONTROLS);
	}, [tickets]);

	const sortedTickets = useMemo(
		() =>
			sortTickets(tickets, controls, (ticket, sortBy) => {
				switch (sortBy) {
					case 'key':
						return ticket.key;
					case 'summary':
						return ticket.summary;
					case 'priority':
						return ticket.priority;
					case 'status':
						return ticket.status;
					case 'targetDate':
						return ticket.targetDate;
					default:
						return '';
				}
			}),
		[controls, tickets],
	);

	const visibleTickets = useMemo(() => {
		const firstVisibleIndex = controls.page * controls.size;

		return sortedTickets.slice(firstVisibleIndex, firstVisibleIndex + controls.size);
	}, [controls.page, controls.size, sortedTickets]);

	const head = useMemo<TableHeads>(
		() => ({
			cells: [
				{
					key: 'key',
					content: (
						<LinkedTicketsTableHeading message={messages.ticketKeyColumnHeading} intl={intl} />
					),
					isSortable: true,
					width: 12,
				},
				{
					key: 'summary',
					content: (
						<LinkedTicketsTableHeading message={messages.ticketSummaryColumnHeading} intl={intl} />
					),
					isSortable: true,
					width: 40,
				},
				{
					key: 'priority',
					content: (
						<LinkedTicketsTableHeading message={messages.ticketPriorityColumnHeading} intl={intl} />
					),
					isSortable: true,
					width: 12,
				},
				{
					key: 'status',
					content: (
						<LinkedTicketsTableHeading message={messages.ticketStatusColumnHeading} intl={intl} />
					),
					isSortable: true,
					width: 20,
				},
				{
					key: 'targetDate',
					content: (
						<LinkedTicketsTableHeading
							message={messages.ticketTargetDateColumnHeading}
							intl={intl}
						/>
					),
					isSortable: true,
					width: 16,
				},
			],
		}),
		[intl],
	);

	const rows = useMemo<TableRows>(
		() =>
			visibleTickets.map((ticket) => ({
				key: ticket.key,
				cells: [
					{
						key: 'key',
						content: (
							<Text size="small">
								<Link
									href={ticket.url}
									target="_blank"
									onClick={() => onTicketLinkClick(ticket.key)}
								>
									{ticket.key}
								</Link>
							</Text>
						),
					},
					{
						key: 'summary',
						content: (
							<Tooltip content={ticket.summary}>
								<Box xcss={styles['ticketSummary']}>
									<Text size="small">{ticket.summary}</Text>
								</Box>
							</Tooltip>
						),
					},
					{
						key: 'priority',
						content: ticket.priority ? (
							<Lozenge appearance={getPriorityLozengeAppearance(ticket.priority)}>
								P{ticket.priority}
							</Lozenge>
						) : (
							<MissingTicketValue intl={intl} />
						),
					},
					{
						key: 'status',
						content: (
							<Lozenge appearance={getStatusLozengeAppearance(ticket.status)}>
								{ticket.status}
							</Lozenge>
						),
					},
					{
						key: 'targetDate',
						content: <TicketDate value={ticket.targetDate} intl={intl} />,
					},
				],
			})),
		[intl, onTicketLinkClick, visibleTickets],
	);

	return (
		<FastshiftTable
			id={`cs-profiler-ent-tickets-${getThemeIdSegment(themeName)}`}
			testId={`cs-profiler-ent-tickets-${getThemeIdSegment(themeName)}`}
			tableCaption={intl.formatMessage(messages.entTicketsTableCaption, { themeName })}
			isTableLoading={false}
			isTableError={false}
			errorComponent={null}
			head={head}
			rows={rows}
			paginationTextSize="small"
			density="compact"
			controls={{
				pagination: getPagination(controls, tickets.length),
				onSetControlsPayload: setControls,
				controls,
			}}
		/>
	);
};

const JpdTicketsTable = ({
	tickets,
	themeName,
	intl,
	onTicketLinkClick,
}: {
	tickets: readonly FastshiftCsProfilerJpdTicket[];
	themeName: string;
	intl: IntlShape;
	onTicketLinkClick: (ticketKey: string) => void;
}) => {
	const [controls, setControls] = useState<ControlsPayload>(LINKED_TICKETS_DEFAULT_CONTROLS);

	useEffect(() => {
		setControls(LINKED_TICKETS_DEFAULT_CONTROLS);
	}, [tickets]);

	const sortedTickets = useMemo(
		() =>
			sortTickets(tickets, controls, (ticket, sortBy) => {
				switch (sortBy) {
					case 'key':
						return ticket.key;
					case 'summary':
						return ticket.summary;
					case 'status':
						return ticket.status;
					case 'updated':
						return ticket.updated;
					default:
						return '';
				}
			}),
		[controls, tickets],
	);

	const visibleTickets = useMemo(() => {
		const firstVisibleIndex = controls.page * controls.size;

		return sortedTickets.slice(firstVisibleIndex, firstVisibleIndex + controls.size);
	}, [controls.page, controls.size, sortedTickets]);

	const head = useMemo<TableHeads>(
		() => ({
			cells: [
				{
					key: 'key',
					content: (
						<LinkedTicketsTableHeading message={messages.ticketKeyColumnHeading} intl={intl} />
					),
					isSortable: true,
					width: 12,
				},
				{
					key: 'summary',
					content: (
						<LinkedTicketsTableHeading message={messages.ticketSummaryColumnHeading} intl={intl} />
					),
					isSortable: true,
					width: 50,
				},
				{
					key: 'status',
					content: (
						<LinkedTicketsTableHeading message={messages.ticketStatusColumnHeading} intl={intl} />
					),
					isSortable: true,
					width: 30,
				},
				{
					key: 'updated',
					content: (
						<LinkedTicketsTableHeading message={messages.ticketUpdatedColumnHeading} intl={intl} />
					),
					isSortable: true,
					width: 20,
				},
			],
		}),
		[intl],
	);

	const rows = useMemo<TableRows>(
		() =>
			visibleTickets.map((ticket) => ({
				key: ticket.key,
				cells: [
					{
						key: 'key',
						content: (
							<Text size="small">
								<Link
									href={ticket.url}
									target="_blank"
									onClick={() => onTicketLinkClick(ticket.key)}
								>
									{ticket.key}
								</Link>
							</Text>
						),
					},
					{
						key: 'summary',
						content: (
							<Tooltip content={ticket.summary}>
								<Box xcss={styles['ticketSummary']}>
									<Text size="small">{ticket.summary}</Text>
								</Box>
							</Tooltip>
						),
					},
					{
						key: 'status',
						content: (
							<Lozenge appearance={getStatusLozengeAppearance(ticket.status)}>
								{ticket.status}
							</Lozenge>
						),
					},
					{
						key: 'updated',
						content: <TicketDate value={ticket.updated} intl={intl} />,
					},
				],
			})),
		[intl, onTicketLinkClick, visibleTickets],
	);

	return (
		<FastshiftTable
			id={`cs-profiler-jpd-tickets-${getThemeIdSegment(themeName)}`}
			testId={`cs-profiler-jpd-tickets-${getThemeIdSegment(themeName)}`}
			tableCaption={intl.formatMessage(messages.jpdTicketsTableCaption, { themeName })}
			isTableLoading={false}
			isTableError={false}
			errorComponent={null}
			head={head}
			rows={rows}
			paginationTextSize="small"
			density="compact"
			controls={{
				pagination: getPagination(controls, tickets.length),
				onSetControlsPayload: setControls,
				controls,
			}}
		/>
	);
};

const LinkedTicketsContent = ({
	themeName,
	linkedTicketsState,
	intl,
	onEntTicketLinkClick,
	onJpdTicketLinkClick,
}: {
	themeName: string;
	linkedTicketsState?: CsProfilerLinkedTicketsState;
	intl: IntlShape;
	onEntTicketLinkClick: (ticketKey: string) => void;
	onJpdTicketLinkClick: (ticketKey: string) => void;
}) => {
	const linkedTickets = linkedTicketsState?.data;

	if (linkedTicketsState?.pending && !linkedTickets) {
		return (
			<Text color="color.text.subtle">{intl.formatMessage(messages.linkedTicketsLoading)}</Text>
		);
	}

	if (linkedTicketsState?.error && !linkedTickets) {
		return <Text color="color.text.danger">{intl.formatMessage(messages.linkedTicketsError)}</Text>;
	}

	if (!linkedTickets) {
		return (
			<Text color="color.text.subtle">{intl.formatMessage(messages.linkedTicketsLoading)}</Text>
		);
	}

	if (linkedTickets.entTickets.length === 0 && linkedTickets.jpdTickets.length === 0) {
		return <Text color="color.text.subtle">{intl.formatMessage(messages.noLinkedTickets)}</Text>;
	}

	return (
		<Box xcss={styles['linkedTicketsContentWrapper']}>
			<Stack space="space.150">
				{linkedTicketsState?.error && (
					<Text color="color.text.danger">{intl.formatMessage(messages.linkedTicketsError)}</Text>
				)}
				{linkedTickets.entTickets.length > 0 && (
					<Stack space="space.025">
						<LinkedTicketsTableHeader
							message={messages.entTicketsHeading}
							count={linkedTickets.entTickets.length}
							intl={intl}
						/>
						<EntTicketsTable
							tickets={linkedTickets.entTickets}
							themeName={themeName}
							intl={intl}
							onTicketLinkClick={onEntTicketLinkClick}
						/>
					</Stack>
				)}
				{linkedTickets.jpdTickets.length > 0 && (
					<Stack space="space.025">
						<LinkedTicketsTableHeader
							message={messages.jpdTicketsHeading}
							count={linkedTickets.jpdTickets.length}
							intl={intl}
						/>
						<JpdTicketsTable
							tickets={linkedTickets.jpdTickets}
							themeName={themeName}
							intl={intl}
							onTicketLinkClick={onJpdTicketLinkClick}
						/>
					</Stack>
				)}
			</Stack>
		</Box>
	);
};

export const FastshiftCsProfilerLinkedTickets = ({
	themeName,
}: FastshiftCsProfilerLinkedTicketsProps) => {
	const intl = useIntl();
	const stargateClient = useBxpWebStargateClient();
	const dispatch = useFastshiftDispatch();
	const {
		sendLinkedTicketsToggleClickUIEvent,
		sendLinkedTicketsDetailsTrackEvent,
		sendLinkedTicketLinkClickUIEvent,
	} = useFastshiftCsProfilerAnalytics();
	const linkedTicketsState = useFastshiftSelector(
		(state) => state.csProfiler.linkedTicketsByTheme[themeName],
	);
	const [isExpanded, setIsExpanded] = useState(false);
	const ToggleIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

	useEffect(() => {
		setIsExpanded(false);
	}, [themeName]);

	const handleToggleLinkedTickets = useCallback(() => {
		const shouldExpand = !isExpanded;

		setIsExpanded(shouldExpand);
		sendLinkedTicketsToggleClickUIEvent({ themeName, isExpanded: shouldExpand });

		if (shouldExpand && !linkedTicketsState?.pending && !linkedTicketsState?.data) {
			dispatch(getFastshiftCsProfilerLinkedTickets({ stargateClient, theme: themeName }));
		}
	}, [
		dispatch,
		isExpanded,
		linkedTicketsState,
		sendLinkedTicketsToggleClickUIEvent,
		stargateClient,
		themeName,
	]);

	const handleEntTicketLinkClick = useCallback(
		(ticketKey: string) => {
			sendLinkedTicketLinkClickUIEvent({ themeName, ticketType: 'ent', ticketKey });
		},
		[sendLinkedTicketLinkClickUIEvent, themeName],
	);

	const handleJpdTicketLinkClick = useCallback(
		(ticketKey: string) => {
			sendLinkedTicketLinkClickUIEvent({ themeName, ticketType: 'jpd', ticketKey });
		},
		[sendLinkedTicketLinkClickUIEvent, themeName],
	);

	useEffect(() => {
		if (!isExpanded) {
			return;
		}

		sendLinkedTicketsDetailsTrackEvent({ themeName, linkedTicketsState });
	}, [isExpanded, linkedTicketsState, sendLinkedTicketsDetailsTrackEvent, themeName]);

	return (
		<Box>
			<Stack space="space.050">
				<Pressable
					type="button"
					xcss={styles['linkedTicketsToggleButton']}
					aria-expanded={isExpanded}
					onClick={handleToggleLinkedTickets}
				>
					<Inline alignBlock="center" space="space.050">
						<ToggleIcon label="" color={token('color.link')} size="small" />
						<Text color="color.link">{intl.formatMessage(messages.linkedTicketsButton)}</Text>
					</Inline>
				</Pressable>
				{isExpanded && (
					<LinkedTicketsContent
						themeName={themeName}
						linkedTicketsState={linkedTicketsState}
						intl={intl}
						onEntTicketLinkClick={handleEntTicketLinkClick}
						onJpdTicketLinkClick={handleJpdTicketLinkClick}
					/>
				)}
			</Stack>
		</Box>
	);
};
